import type { SeedIsland } from './seedLatin'

/**
 * Numbers, prices and dates — the four Latin-script languages.
 *
 * Numbers are where a learner's fluency collapses first, and for a reason worth
 * naming: everything else in a conversation can be inferred from context, and a
 * number cannot. Miss a noun and you can still follow. Miss whether the rent was
 * six hundred or six thousand and the conversation is over.
 *
 * They are also the worst thing to try to build from grammar in the moment,
 * because the pressure is highest exactly when the arithmetic is hardest — at a
 * till, on a phone, in front of someone waiting. So these are frames rather than
 * lists: the sentence around the number is what gets drilled, and the number
 * itself is the one part you swap.
 *
 * The confirmation lines matter as much as the asking ones. "Was that thirteen
 * or thirty" is the single most useful thing on either island, because the pair
 * is near-identical in every one of these languages and getting it wrong is
 * expensive.
 */
export const NUMBERS_LATIN: Record<string, SeedIsland[]> = {
  de: [
    {
      slug: 'zahlen',
      title: 'Numbers and prices',
      nativeTitle: 'Zahlen und Preise',
      useWhen: 'At a till, on a phone, anywhere a number decides something.',
      tags: ['transactional', 'numbers'],
      level: 2,
      blocks: [
        [
          'Was kostet das zusammen?',
          'What does that come to altogether?',
          'zusammen — together — is what turns "what does it cost" into "what is the total". Without it you may get the price of one item.',
        ],
        [
          'Ich nehme zwei davon, bitte.',
          "I'll take two of those, please.",
          'davon — of them — saves you naming the thing again, which is the part you are least likely to have ready.',
        ],
        [
          'Entschuldigung, war das dreizehn oder dreißig?',
          'Sorry, was that thirteen or thirty?',
          'The most useful line here. German -zehn and -ßig are as easy to confuse as the English pair, and the cost of guessing is real money.',
        ],
        [
          'Könnten Sie die Zahl bitte aufschreiben?',
          'Could you write the number down, please?',
          'Never embarrassing, always faster. aufschreiben is separable — the prefix rejoins at the end in most tenses.',
        ],
        [
          'Das ist mir ehrlich gesagt zu teuer.',
          "That's honestly too expensive for me.",
          'ehrlich gesagt — honestly speaking — softens a refusal into an opinion. mir is dative: expensive *to me*.',
        ],
        [
          'Zahlen die meisten hier bar oder mit Karte?',
          'Do most people here pay cash or by card?',
          'Asks about the norm rather than announcing your problem, which is more useful and less exposing.',
        ],
      ],
    },
    {
      slug: 'datum',
      title: 'Dates and times',
      nativeTitle: 'Datum und Uhrzeit',
      useWhen: 'Arranging anything. The half of an appointment you cannot guess.',
      tags: ['transactional', 'time'],
      level: 2,
      blocks: [
        [
          'Der Wievielte ist heute?',
          "What's the date today?",
          'A fixed idiom and a strange one — literally "the how-manyeth". Learn it whole; assembling it never works.',
        ],
        [
          'Ich komme am vierzehnten März an.',
          'I arrive on the fourteenth of March.',
          'am + ordinal + -en for a date. ankommen is separable, so an lands at the end.',
        ],
        [
          'Ich wohne seit zwei Jahren hier.',
          "I've lived here for two years.",
          'seit takes the dative and the PRESENT tense — German says "I live here since two years", where English needs a perfect.',
        ],
        [
          'Von Montag bis Freitag passt mir gut.',
          'Monday to Friday suits me well.',
          'von … bis is the fixed pair for a span. passt mir — suits me — is dative again, and reusable for any offer.',
        ],
        [
          'Geht es auch nächste Woche?',
          'Would next week work too?',
          'Geht es — does it go — is how German asks whether something is possible. Endlessly reusable.',
        ],
        [
          'Um wie viel Uhr sollen wir uns treffen?',
          'What time should we meet?',
          'Um wie viel Uhr, three words where English uses one. sich treffen is reflexive: we meet *each other*.',
        ],
      ],
    },
  ],

  fr: [
    {
      slug: 'chiffres',
      title: 'Numbers and prices',
      nativeTitle: 'Chiffres et prix',
      useWhen: 'The one thing in a conversation you cannot work out from context.',
      tags: ['transactional', 'numbers'],
      level: 2,
      blocks: [
        [
          'Ça fait combien en tout ?',
          'What does that come to altogether?',
          'Ça fait — literally "that makes" — is how French asks a total. en tout closes off the "for one?" answer.',
        ],
        [
          "J'en prends deux, s'il vous plaît.",
          "I'll take two, please.",
          "en stands in for the thing itself, and French will not let you drop it: j'en prends deux, never j'prends deux.",
        ],
        [
          'Pardon, vous avez dit soixante ou soixante-dix ?',
          'Sorry, did you say sixty or seventy?',
          'The French pair worth drilling above all others — seventy is literally "sixty-ten", so the two run together at speed.',
        ],
        [
          'Vous pourriez me l’écrire, s’il vous plaît ?',
          'Could you write it down for me, please?',
          'Two pronouns before the verb, in the order French insists on: me then le.',
        ],
        [
          "C'est un peu au-dessus de mon budget.",
          "That's a little above my budget.",
          'Softer than "too expensive" and harder to argue with. au-dessus de — above.',
        ],
        [
          'On peut payer par carte ?',
          'Can one pay by card?',
          'on rather than je — asking about the rule rather than about yourself, which is both more natural and less exposing.',
        ],
      ],
    },
    {
      slug: 'dates',
      title: 'Dates and times',
      nativeTitle: 'Dates et heures',
      useWhen: 'Every arrangement rests on this, and none of it can be guessed.',
      tags: ['transactional', 'time'],
      level: 2,
      blocks: [
        [
          'On est quel jour aujourd’hui ?',
          "What's the date today?",
          'Literally "what day are we on". The idiom uses on est, never il est, and swapping them marks you instantly.',
        ],
        [
          "J'arrive le quatorze mars.",
          'I arrive on the fourteenth of March.',
          'le + plain number + month. No ordinal and no preposition — French says "the fourteen March". The one exception is le premier.',
        ],
        [
          "J'habite ici depuis deux ans.",
          "I've lived here for two years.",
          'depuis with the PRESENT tense, exactly like German seit. Using a past tense here is the commonest English mistake in French.',
        ],
        [
          'Du lundi au vendredi, ça me va.',
          'Monday to Friday works for me.',
          'du … au for a span — note the contractions. ça me va is the shortest way to accept anything.',
        ],
        [
          'Est-ce que la semaine prochaine serait possible ?',
          'Would next week be possible?',
          'The conditional serait rather than est: it asks rather than checks, which is the difference between polite and blunt.',
        ],
        [
          'À quelle heure est-ce qu’on se retrouve ?',
          'What time are we meeting?',
          'se retrouver — to meet up again — is warmer than se rencontrer, which is for meeting someone new.',
        ],
      ],
    },
  ],

  es: [
    {
      slug: 'numeros',
      title: 'Numbers and prices',
      nativeTitle: 'Números y precios',
      useWhen: 'Where fluency fails first, because nothing here can be inferred.',
      tags: ['transactional', 'numbers'],
      level: 2,
      blocks: [
        [
          '¿Cuánto es en total?',
          'How much is it altogether?',
          'es rather than cuesta — Spanish uses "to be" for a total. en total closes off a per-item answer.',
        ],
        [
          'Me llevo dos, por favor.',
          "I'll take two, please.",
          'llevarse — to carry off — is the verb for taking something you are buying. Not tomar, which is drinking or catching.',
        ],
        [
          'Perdone, ¿ha dicho sesenta o setenta?',
          'Sorry, did you say sixty or seventy?',
          'The Spanish pair that costs money. sesenta and setenta differ by one consonant and run together at speed.',
        ],
        [
          '¿Me lo puede apuntar, por favor?',
          'Could you write it down for me, please?',
          'apuntar — to note down — is the everyday word. Me lo: to me, it, in that order.',
        ],
        [
          'Se me va un poco de presupuesto.',
          "That's a bit beyond my budget.",
          'The impersonal se again: it goes away from me, rather than "I cannot afford it". Spanish prefers the blame elsewhere.',
        ],
        [
          '¿Se puede pagar con tarjeta?',
          'Can one pay by card?',
          'Se puede — is it possible — asks about the rule rather than about you.',
        ],
      ],
    },
    {
      slug: 'fechas',
      title: 'Dates and times',
      nativeTitle: 'Fechas y horas',
      useWhen: 'Arranging anything at all. None of it survives improvisation.',
      tags: ['transactional', 'time'],
      level: 2,
      blocks: [
        [
          '¿A qué día estamos?',
          "What's the date today?",
          'Literally "on what day are we". estamos, not es — Spanish uses estar for a date, and the idiom is fixed.',
        ],
        [
          'Llego el catorce de marzo.',
          'I arrive on the fourteenth of March.',
          'el + plain number + de + month. No ordinal, exactly as in French, except for el primero.',
        ],
        [
          'Llevo dos años viviendo aquí.',
          "I've been living here for two years.",
          'llevar + time + gerund is the Spanish way to say how long something has been going on. There is no neat English equivalent.',
        ],
        [
          'De lunes a viernes me viene bien.',
          'Monday to Friday suits me.',
          'de … a for a span, with no articles on the days. venir bien — to come well — is the everyday "suits me".',
        ],
        [
          '¿Sería posible la semana que viene?',
          'Would next week be possible?',
          'sería is the conditional, which asks rather than demands. la semana que viene — the week that is coming.',
        ],
        [
          '¿A qué hora quedamos?',
          'What time shall we meet?',
          'quedar means to arrange to meet, and is one of the most-used verbs in spoken Spanish. Nothing in English maps onto it cleanly.',
        ],
      ],
    },
  ],

  it: [
    {
      slug: 'numeri',
      title: 'Numbers and prices',
      nativeTitle: 'Numeri e prezzi',
      useWhen: 'A price is the one thing in a conversation you cannot guess at.',
      tags: ['transactional', 'numbers'],
      level: 2,
      blocks: [
        [
          "Quant'è in tutto?",
          'How much is it altogether?',
          "Quant'è — the elision of quanto è — is how Italians actually ask. Saying it in full sounds like reading.",
        ],
        [
          'Ne prendo due, grazie.',
          "I'll take two, thanks.",
          'ne stands in for the thing, exactly like French en, and Italian will not let you drop it either.',
        ],
        [
          'Scusi, ha detto sessanta o settanta?',
          'Sorry, did you say sixty or seventy?',
          'One consonant apart, and Italian doubles it — sessanta against settanta. Doubled consonants change the word here.',
        ],
        [
          'Me lo può scrivere, per favore?',
          'Could you write it down for me, please?',
          'mi becomes me before another pronoun. Italian does this automatically and it catches everyone at first.',
        ],
        [
          'È un po’ fuori dal mio budget.',
          "That's a bit outside my budget.",
          'fuori da — outside of. Softer than caro, and it makes the budget the problem rather than the price.',
        ],
        [
          'Si può pagare con la carta?',
          'Can one pay by card?',
          'The impersonal si, asking about what is done rather than what you can do.',
        ],
      ],
    },
    {
      slug: 'date',
      title: 'Dates and times',
      nativeTitle: 'Date e orari',
      useWhen: 'The half of every arrangement that cannot be improvised.',
      tags: ['transactional', 'time'],
      level: 2,
      blocks: [
        [
          'Quanti ne abbiamo oggi?',
          "What's the date today?",
          'Literally "how many of them do we have today" — an idiom with no logic to reconstruct. Learn it whole.',
        ],
        [
          'Arrivo il quattordici marzo.',
          'I arrive on the fourteenth of March.',
          'il + plain number + month, no preposition. Only the first of the month takes an ordinal: il primo.',
        ],
        [
          'Abito qui da due anni.',
          "I've lived here for two years.",
          'da with the PRESENT tense, like German seit and French depuis. The Romance and Germanic languages agree here and English is the odd one out.',
        ],
        [
          'Dal lunedì al venerdì mi va bene.',
          'Monday to Friday works for me.',
          'dal … al, with the articles fused to the prepositions. mi va bene is the all-purpose acceptance.',
        ],
        [
          'Sarebbe possibile la settimana prossima?',
          'Would next week be possible?',
          'sarebbe, the conditional — it asks. È possibile would be checking a fact.',
        ],
        [
          'A che ora ci vediamo?',
          'What time shall we meet?',
          'ci vediamo — we see each other — is both "what time shall we meet" and, on its own, "see you". Worth having for both.',
        ],
      ],
    },
  ],
}
